
































































































































import {Component, Mixins} from 'vue-property-decorator'
import {MixinScreenSize} from '@/components/mixins/MixinScreenSize'
import {BridgeStep} from '@/enums/BridgeStep'
import {$} from '@/facade'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'
import {
  BridgeTrackingHelper,
  TBridgeLegStatus,
} from '@/helpers/BridgeTrackingHelper'
import {
  getN3InvocationBuilder,
  getXInvocationBuilder,
} from '@/model/wallets/invocationsBuilder'
import {BridgeNetworks} from '@/model/bridge/types/BridgeNetworks'
import {isRejectedByUserError} from '@/model/wallets/types/WalletErrors'
import {BridgeTokenOption} from '@/components/BridgeTokenSelection.vue'
import BridgeLoadingStep from '@/components/BridgeLoadingStep.vue'
import BridgeErrorStep from '@/components/BridgeErrorStep.vue'
import BridgeTrackingStep from '@/components/BridgeTrackingStep.vue'
import BridgeApprovedRequiredStep from '@/components/BridgeApprovedRequiredStep.vue'

@Component({
  components: {
    BridgeErrorStep,
    BridgeTrackingStep,
    BridgeLoadingStep,
    BridgeApprovedRequiredStep,
  },
  computed: {
    BridgeStep() {
      return BridgeStep
    },
  },
})
export default class BridgeModal extends Mixins(MixinScreenSize) {
  currentStep = BridgeStep.LOADING
  sourceNetwork: BridgeNetworks = 'N3'

  sourceStatus: TBridgeLegStatus = 'idle'
  sourceTransactionHash: string | null = null

  targetStatus: TBridgeLegStatus = 'idle'
  targetTransactionHash: string | null = null

  private trackingToken = 0

  get canBeClosed() {
    return (
      this.currentStep !== BridgeStep.LOADING &&
      this.currentStep !== BridgeStep.WAITING_SIGNATURE &&
      this.currentStep !== BridgeStep.APPROVED_REQUIRED
    )
  }

  async openEvent({
    sourceNetwork,
    amount,
    selectedToken,
  }: {
    sourceNetwork: BridgeNetworks
    amount: string
    selectedToken: BridgeTokenOption
  }) {
    this.trackingToken += 1
    this.sourceNetwork = sourceNetwork

    const isNativeToken = selectedToken.symbol === 'GAS'

    if (sourceNetwork === 'X') {
      await (isNativeToken
        ? this.withdrawNative(amount)
        : this.withdrawToken({amount, selectedToken}))
      return
    }

    await (isNativeToken
      ? this.depositNative(amount)
      : this.depositToken({amount, selectedToken}))
  }

  async closeEvent() {
    this.trackingToken += 1
    this.resetValues()

    this.$emit('close')
  }

  private resetValues() {
    this.currentStep = BridgeStep.LOADING
    this.sourceNetwork = 'N3'
    this.sourceStatus = 'idle'
    this.sourceTransactionHash = null
    this.targetStatus = 'idle'
    this.targetTransactionHash = null
  }

  private async withdrawToken({
    amount,
    selectedToken,
  }: {
    amount: string
    selectedToken: BridgeTokenOption
  }) {
    try {
      const network = BridgeNetworkHelper.network()
      const neoXInvocationBuilder = getXInvocationBuilder(network)
      const tokenDecimals =
        selectedToken.x.chainDecimals ?? selectedToken.x.decimals

      const needsApproval = await neoXInvocationBuilder.needsApproval({
        tokenHash: selectedToken.x.hash,
        amount,
        tokenDecimals,
      })

      if (needsApproval) {
        this.currentStep = BridgeStep.APPROVED_REQUIRED

        const approveInvocation = neoXInvocationBuilder.approveInvocation({
          amount,
          tokenHash: selectedToken.x.hash,
          tokenDecimals,
        })

        const approvalTransactionHash = await $.walletAdapter.invokeXWallet(
          approveInvocation,
          network
        )
        if (!approvalTransactionHash) throw new Error('Undefined transaction')

        this.currentStep = BridgeStep.LOADING

        const approvalReceipt = await BridgeTrackingHelper.waitForNeoXReceipt(
          approvalTransactionHash
        )
        if (!approvalReceipt) throw new Error('Approval was not mined')
        if (approvalReceipt.status !== 1) {
          throw new Error('Approval transaction failed')
        }
      }

      this.currentStep = BridgeStep.WAITING_SIGNATURE

      const withdrawTokenInvocation = await neoXInvocationBuilder.withdrawTokenInvocation(
        {
          amount,
          tokenHash: selectedToken.x.hash,
          tokenDecimals,
        }
      )

      const transactionHash = await $.walletAdapter.invokeXWallet(
        withdrawTokenInvocation,
        network
      )
      if (!transactionHash) throw new Error('Undefined transaction')

      await this.trackWithdraw({
        transactionHash,
        n3TokenHash: selectedToken.n3.hash,
      })
    } catch (error) {
      this.threatError(error)
    }
  }

  private async withdrawNative(amount: string) {
    try {
      const network = BridgeNetworkHelper.network()
      const neoXInvocationBuilder = getXInvocationBuilder(network)
      const withdrawNativeInvocation = await neoXInvocationBuilder.withdrawNativeInvocation(
        {
          amount,
        }
      )

      this.currentStep = BridgeStep.WAITING_SIGNATURE

      const transactionHash = await $.walletAdapter.invokeXWallet(
        withdrawNativeInvocation,
        network
      )
      if (!transactionHash) throw new Error('Undefined transaction')

      await this.trackWithdraw({transactionHash, n3TokenHash: null})
    } catch (error) {
      this.threatError(error)
    }
  }

  private async depositToken({
    amount,
    selectedToken,
  }: {
    amount: string
    selectedToken: BridgeTokenOption
  }) {
    try {
      const network = BridgeNetworkHelper.network()
      const n3InvocationBuilder = getN3InvocationBuilder(network)
      const depositTokenInvocation = n3InvocationBuilder.depositTokenInvocation(
        {
          amount: String(amount),
          tokenDecimals: selectedToken.n3.decimals,
          tokenHash: selectedToken.n3.hash,
        }
      )

      this.currentStep = BridgeStep.WAITING_SIGNATURE

      const transactionHash = await $.walletAdapter.invokeN3Wallet(
        depositTokenInvocation,
        network
      )
      if (!transactionHash) throw new Error('Undefined transaction')

      await this.trackDeposit({
        transactionHash,
        xTokenHash: selectedToken.x.hash,
      })
    } catch (error) {
      this.threatError(error)
    }
  }

  private async depositNative(amount: string) {
    try {
      const network = BridgeNetworkHelper.network()
      const n3InvocationBuilder = getN3InvocationBuilder(network)
      const depositNativeInvocationBuilder = n3InvocationBuilder.depositNativeInvocation(
        amount
      )

      this.currentStep = BridgeStep.WAITING_SIGNATURE

      const transactionHash = await $.walletAdapter.invokeN3Wallet(
        depositNativeInvocationBuilder,
        network
      )
      if (!transactionHash) throw new Error('Undefined transaction')

      await this.trackDeposit({transactionHash, xTokenHash: null})
    } catch (error) {
      this.threatError(error)
    }
  }

  private async trackDeposit({
    transactionHash,
    xTokenHash,
  }: {
    transactionHash: string
    xTokenHash: string | null
  }) {
    const token = this.trackingToken

    this.sourceTransactionHash = transactionHash
    this.sourceStatus = 'pending'
    this.currentStep = BridgeStep.TRACKING

    const shouldStop = () => this.trackingToken !== token

    const execution = await BridgeTrackingHelper.waitForN3Execution(
      transactionHash,
      await BridgeNetworkHelper.n3Rpc(),
      shouldStop
    )
    if (shouldStop()) return

    if (!execution) {
      this.sourceStatus = 'timeout'
      return
    }

    if (execution.vmState !== 'HALT' || execution.exception !== null) {
      this.sourceStatus = 'error'
      return
    }

    this.sourceStatus = 'success'

    const nonce = BridgeTrackingHelper.depositNonceOf(execution, !xTokenHash)
    if (!nonce) {
      this.targetStatus = 'error'
      return
    }

    this.targetStatus = 'pending'

    const releaseTransactionHash = await BridgeTrackingHelper.waitForNeoXRelease(
      {nonce, tokenHash: xTokenHash, shouldStop}
    )
    if (shouldStop()) return

    if (!releaseTransactionHash) {
      this.targetStatus = 'timeout'
      return
    }

    this.targetTransactionHash = releaseTransactionHash
    this.targetStatus = 'success'
  }

  private async trackWithdraw({
    transactionHash,
    n3TokenHash,
  }: {
    transactionHash: string
    n3TokenHash: string | null
  }) {
    const token = this.trackingToken

    this.sourceTransactionHash = transactionHash
    this.sourceStatus = 'pending'
    this.currentStep = BridgeStep.TRACKING

    const shouldStop = () => this.trackingToken !== token

    const receipt = await BridgeTrackingHelper.waitForNeoXReceipt(
      transactionHash,
      shouldStop
    )
    if (shouldStop()) return

    if (!receipt) {
      this.sourceStatus = 'timeout'
      return
    }

    if (receipt.status !== 1) {
      this.sourceStatus = 'error'
      return
    }

    this.sourceStatus = 'success'

    const nonce = BridgeTrackingHelper.withdrawNonceOf(receipt)
    if (!nonce) {
      this.targetStatus = 'error'
      return
    }

    this.targetStatus = 'pending'

    const release = await BridgeTrackingHelper.waitForN3Release({
      nonce,
      tokenHash: n3TokenHash,
      shouldStop,
    })
    if (shouldStop()) return

    if (!release) {
      this.targetStatus = 'timeout'
      return
    }

    this.targetTransactionHash = release.transactionHash
    this.targetStatus = release.vmState === 'HALT' ? 'success' : 'error'
  }

  private threatError(error: any) {
    console.error(error)

    if (isRejectedByUserError(error)) {
      this.currentStep = BridgeStep.REJECTED_BY_THE_USER
      return
    }

    const hasInsufficientFunds =
      error.message && String(error.message).includes('insufficient funds')
    if (hasInsufficientFunds) {
      this.currentStep = BridgeStep.INSUFFICIENT_FUNDS
      return
    }

    this.currentStep = BridgeStep.UNKNOWN_ERROR
  }
}
